import React, { createContext, useState, useContext, ReactNode } from 'react';
import { AlertColor } from '@mui/material';

interface AlertContextType {
    showAlert: (message: string, severity: AlertColor) => void;
    hideAlert: () => void;
    alertState: {
        open: boolean;
        message: string;
        severity: AlertColor;
    };
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlert = () => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
};

interface AlertProviderProps {
    children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        severity: 'warning' as AlertColor,
    });

    const showAlert = (message: string, severity: AlertColor) => {
        setAlertState({ open: true, message, severity });
    };

    const hideAlert = () => {
        setAlertState((prev) => ({ ...prev, open: false }));
    };

    // Listen for global alert events
    React.useEffect(() => {
        const handleGlobalAlert = (event: CustomEvent) => {
            const { message, type } = event.detail;
            showAlert(message, type);
        };

        window.addEventListener('showGlobalAlert', handleGlobalAlert as EventListener);
        
        return () => {
            window.removeEventListener('showGlobalAlert', handleGlobalAlert as EventListener);
        };
    }, []);

    return (
        <AlertContext.Provider value={{ showAlert, hideAlert, alertState }}>
            {children}
        </AlertContext.Provider>
    );
};